<template>
	<DashboardItem :header="$t('dashboard.baseStationStatuses.header')" :timing="$t('dashboard.baseStationStatuses.timing')">
		<Row>
			<Col :span="12">
				<InteractivePieChart
					:data="stats"
					:size="300"
					v-model:selectedIndices="selectedIndices"
				/>
			</Col>
			<Col :span="12">
				<div class="selected-stations-container">
					<TypographyTitle :level="5">{{ $t('dashboard.baseStationStatuses.selected', selectedTotal) }}</TypographyTitle>
					<div class="selected-stations-list-wrapper">
						<div class="selected-stations">
							<MountpointDisplayRich
								v-for="baseStation in selectedBaseStations"
								:key="baseStation.id"
								:baseStation="baseStation"
							/>
						</div>
					</div>
				</div>
			</Col>
		</Row>
	</DashboardItem>
</template>

<script>
import { ref, computed } from 'vue';
import { TypographyTitle, Col, Row } from 'ant-design-vue';

import Utils from '@/api/Utils';

import { useI18n } from 'vue-i18n';

import InteractivePieChart from '@/components/charts/InteractivePieChart.vue';
import MountpointDisplayRich from '@/components/baseStations/MountpointDisplayRich.vue';
import DashboardItem from '@/components/dashboard/DashboardItem.vue';

import { useBaseStationsStore } from '@/stores/baseStations';


const CHART_CATEGORIES = [
	{ key: 'online-no-breaks', color: '#52C41A', condition: baseStation => baseStation.status === 'working' && baseStation.status_online === 'online'},
	{ key: 'online-with-breaks', color: '#FAAD14', condition: baseStation => baseStation.status === 'working' && baseStation.status_online === 'some_breaks'},
	{ key: 'online-no-measurements', color: '#FF7F27', condition: baseStation => (
		baseStation.status === 'working' && baseStation.status_live === 'live_data' && ['broken', 'offline'].includes(baseStation.status_online)
	)},
	{ key: 'broken', color: '#FF4D4F', condition: baseStation => baseStation.status === 'working' && baseStation.status_live === 'no_live_data'},
	{ key: 'offline', color: '#d9d9d9', condition: baseStation => ['stopped', 'failed', 'deleted'].includes(baseStation.status) },
	{ key: 'others', color: '#1890ff', condition: () => true},
]


export default {
	components: {
		Row, Col, TypographyTitle,
		InteractivePieChart,
		MountpointDisplayRich,
		DashboardItem,
	},
	setup() {
		const { t } = useI18n();
		const selectedIndices = ref([0, 1]);
		const selectedTotal = computed(() => stats.value
			.filter((_, index) => selectedIndices.value.includes(index))
			.reduce((sum, item) => sum + item.value, 0)
		);
		const baseStationsStore = useBaseStationsStore();
		const stats = computed(() => {
			let resultMap = new Map();
			CHART_CATEGORIES.forEach(category => {
				resultMap.set(category.key, []);
			});
			baseStationsStore.items.forEach(baseStation => {
				for (const category of CHART_CATEGORIES) {
					if (category.condition(baseStation)) {
						resultMap.get(category.key).push({
							id: baseStation.id,
							label: baseStation.label,
							mntpnt_int: baseStation.mntpnt_int,
							mntpnt_src: baseStation.mntpnt_src,
						});
						break;
					}
				}
			});
			return CHART_CATEGORIES.map(category => ({
				label: t(`dashboard.baseStationStatuses.categories.${category.key}`),
				value: resultMap.get(category.key).length,
				color: category.color,
				baseStations: resultMap.get(category.key),
			}));
		});
		const selectedBaseStations = computed(() => {
			let result = stats.value
				.filter((_, index) => selectedIndices.value.includes(index))
				.map(item => item.baseStations)
				.flat();
			result.sort(Utils.objectFieldMultiSorter('mntpnt_int'));
			return result;
		});
		return {
			stats,
			selectedIndices,
			selectedTotal,
			selectedBaseStations,
		};
	},
}
</script>

<style scoped>
.selected-stations-container {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 0;
	padding-inline-start: 8px;
	height: 100%;
}
.selected-stations-list-wrapper {
	flex-grow: 1;
	position: relative;
}
.selected-stations {
	position: absolute;
	inset: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 8px;
	border: 1px solid #d9d9d9;
	background-color: white;
}
</style>
